<template>
    <section class="relative overflow-hidden">

        <!-- Sfondo decorativo, tiene la sezione legata all'oro del brand -->
        <div class="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-primary/10 to-transparent dark:from-primary/[0.07]"></div>

        <WrapperLayout>

            <!-- Intestazione -->
            <h1 class="heading text-center !text-3xl lg:!text-4xl">
                La Classifica
            </h1>

            <!-- Stagione: arriva insieme alla classifica, quindi la mostro solo se il backend la fornisce -->
            <div v-if="stagione" class="mb-6 flex justify-center">
                <p class="inline-flex items-center gap-2 rounded-full bg-primary/20 px-4 py-1.5 text-sm font-bold uppercase tracking-widest text-amber-800 dark:bg-primary/15 dark:text-primary">
                    <span class="material-symbols-outlined material-symbols-font-300 !text-base"> calendar_month </span>
                    Stagione {{ stagione }}
                </p>
            </div>

            <p class="mx-auto mb-10 max-w-2xl text-center text-lg text-gray-500 dark:text-gray-200">
                Ecco chi sta dominando il Fanta Blondin. Cerca la tua squadra e scopri
                quanto ti separa dalla vetta.
            </p>

            <!-- Errore di caricamento -->
            <div
                v-if="error"
                class="mb-8 flex flex-col items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-center sm:flex-row sm:text-left dark:border-red-900/60 dark:bg-red-950/40"
            >
                <div class="flex items-center gap-3">
                    <span class="material-symbols-outlined text-red-500"> error </span>
                    <div>
                        <p class="font-semibold text-red-700 dark:text-red-300">Classifica non disponibile</p>
                        <p class="text-sm text-red-600/80 dark:text-red-400/80">{{ error }}</p>
                    </div>
                </div>
                <Button label="Riprova" severity="danger" outlined size="small" @click="reload" />
            </div>

            <!-- Podio -->
            <div class="mb-10">
                <RankingPodium :standings="podium" :loading="podiumLoading" />
            </div>

            <!-- Tabella completa -->
            <RankingTable
                :teams="teams"
                :totalRecords="totalRecords"
                :first="first"
                :rows="rows"
                :loading="loading"
                v-model:search="search"
                @page="onPage"
            />

        </WrapperLayout>

    </section>
</template>

<script setup>

    // based imports
    import { ref, onMounted, watch, inject } from 'vue';

    // services imports
    import { useDebounceFn } from '@vueuse/core';

    // components imports
    import RankingPodium from './components/RankingPodium.vue';
    import RankingTable from './components/RankingTable.vue';

    // servizi iniettati
    const HttpService = inject('HttpService');

    // colonne richieste al backend
    const COLUMNS = ['posizione', 'squadra', 'punti_totali'];

    // stagione corrente, restituita da getClassifica
    const stagione = ref(null);

    // stato del podio (le prime tre squadre, caricate a parte e mai filtrate)
    const podium = ref([]);
    const podiumLoading = ref(true);

    // stato della tabella
    const teams = ref([]);
    const totalRecords = ref(0);
    const first = ref(0);
    const rows = ref(20);
    const search = ref('');
    const loading = ref(true);
    const error = ref(null);

    /*
        Contatore delle richieste: se l'utente digita in fretta le risposte possono
        arrivare fuori ordine, quindi tengo solo quella dell'ultima richiesta partita.
    */
    let lastRequestId = 0;

    // chiamata unica verso getClassifica
    const fetchClassifica = async ({ first: offset = 0, rows: limit = 20, term = null }) => {

        const response = await HttpService.post('getClassifica', {
            first: offset,
            rows: limit,
            sortField: null,
            sortOrder: null,
            filters: null,
            ...(term ? { uniqueFilter: term } : {}),
            columnVisible: COLUMNS
        });

        return {
            data: Array.isArray(response?.data) ? response.data : [],
            totalRecords: response?.totalRecords ? parseInt(response.totalRecords) : 0,
            // la chiave `stagione` esiste solo dalla versione del backend che la calcola
            stagione: response?.stagione?.stagione || null
        };
    };

    // carico il podio: prime tre posizioni, l'ordinamento è già garantito dal backend
    const loadPodium = async () => {
        try {
            podiumLoading.value = true;
            const response = await fetchClassifica({ first: 0, rows: 3 });
            podium.value = response.data;
            stagione.value = response.stagione;
        } catch (err) {
            import.meta.env.VITE_DEVELOPMENT === 'true' && console.error(err);
            podium.value = [];
        } finally {
            podiumLoading.value = false;
        }
    };

    // carico la pagina corrente della tabella
    const loadPage = async () => {

        const requestId = ++lastRequestId;

        try {
            loading.value = true;
            error.value = null;

            const term = search.value.trim() || null;
            const response = await fetchClassifica({ first: first.value, rows: rows.value, term });

            // scarto le risposte superate da una richiesta più recente
            if (requestId !== lastRequestId) {
                return;
            }

            teams.value = response.data;
            totalRecords.value = response.totalRecords;

        } catch (err) {
            import.meta.env.VITE_DEVELOPMENT === 'true' && console.error(err);

            if (requestId !== lastRequestId) {
                return;
            }

            error.value = err?.message || 'Riprova tra qualche istante.';
            teams.value = [];
            totalRecords.value = 0;

        } finally {
            requestId === lastRequestId && (loading.value = false);
        }
    };

    // cambio pagina o righe per pagina dal paginatore
    const onPage = (event) => {
        first.value = event?.first ?? 0;
        rows.value = event?.rows ?? rows.value;
        loadPage();
    };

    // ricerca con debounce, ripartendo sempre dalla prima pagina
    const onSearch = useDebounceFn(() => {
        first.value = 0;
        loadPage();
    }, 350);

    watch(search, () => onSearch());

    // ricarico tutto (pulsante "Riprova")
    const reload = async () => {
        first.value = 0;
        await Promise.all([loadPodium(), loadPage()]);
    };

    onMounted(() => {
        loadPodium();
        loadPage();
    });

</script>
